// src/components/pages/SignInPage.js

import React, { useState, useEffect } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useTheme } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom"; // For navigation
import AuthComponent from "../molecules/AuthComponent";
import { fetchCustomToken } from "../../utils/azureUtils";
import useStore from "../../store/useStore";
import useAuthStore from "../../hooks/useAuthStore";
import useUserProfile from "../../hooks/useUserProfile"; // Import the user profile hook
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { fetchUserProfileFromFirestore } from "../../services/profileService"; // Import the service to fetch Firebase profile

const SignInPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setUser } = useAuthStore();
  const { setUserProfile } = useStore(); // Zustand action to set user profile
  const auth = getAuth();
  const [authenticatedUser, setAuthenticatedUser] = useState(null); // To hold the authenticated user
  const isConnectRoute = location.pathname === "/challenge/connect";
  const [pairingCode, setPairingCode] = useState("");

  // Extract the code from the URL on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get("code");
    if (codeFromUrl) {
      const upperCaseCode = codeFromUrl.toUpperCase(); // Convert to uppercase
      setPairingCode(upperCaseCode); // Set the uppercase value in state
      console.log("Pairing code from URL:", upperCaseCode);
    }
  }, [location.search]);

  // Handle code complete from AuthComponent
  const handleCodeComplete = (completeCode) => {
    setPairingCode(completeCode); // Set the complete code if needed
    console.log("Complete pairing code from AuthComponent:", completeCode);
    // Proceed with authentication or other logic using completeCode
    // For example, you might want to create a session here
    // If you need to trigger authentication based on the code, implement it here
  };

  // console.log("isConnectRoute: ", isConnectRoute);

  // Fetch user profile after user has authenticated
  const {
    userProfile,
    loading: profileLoading,
    error: profileError,
  } = useUserProfile(authenticatedUser ? authenticatedUser.uid : null);

  // Extract the 'from' location from state
  const from = location.state?.from?.pathname || "/challenge";

  // Redirect based on user profile state
  useEffect(() => {
    const redirectAfterProfileCheck = async () => {
      // Function to handle Firebase profile fetch and Zustand update
      const updateUserProfileFromFirestore = async (user) => {
        try {
          const firestoreProfile = await fetchUserProfileFromFirestore(
            user.uid
          );
          if (firestoreProfile) {
            setUserProfile(firestoreProfile); // Update Zustand with Firebase profile
            return true; // Firebase profile exists
          }
          return false; // Firebase profile does not exist
        } catch (error) {
          console.error("Error fetching profile from Firestore:", error);
          return false; // If error occurs, assume no profile
        }
      };

      if (!profileLoading && authenticatedUser) {
        const firebaseProfileExists = await updateUserProfileFromFirestore(
          authenticatedUser
        ); // Check Firebase profile and update Zustand

        if (firebaseProfileExists) {
          navigate(from, { replace: true }); // Redirect to 'from' location
        } else if (userProfile) {
          navigate(from, { replace: true }); // Redirect to 'from' location if Zustand profile exists
        } else {
          navigate("/challenge/profile/edit", { replace: true }); // Redirect to profile creation if no profile exists
        }
        setLoading(false);
      }

      if (profileError) {
        setError("Error fetching user profile.");
        setLoading(false);
      }
    };

    redirectAfterProfileCheck();
  }, [
    profileLoading,
    userProfile,
    profileError,
    authenticatedUser,
    setUserProfile,
    navigate,
    from, // Include 'from' as a dependency
  ]);

  // Function to map Firebase errors to friendly messages
  const getFriendlyErrorMessage = (errorCode) => {
    switch (errorCode) {
      case "auth/user-not-found":
        return "No account found with this email address.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/email-already-in-use":
        return "An account with this email already exists.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";
      default:
        return "An unexpected error occurred. Please try again later.";
    }
  };

  // Helper function to post form data to the API
  const postFormData = async (url, data) => {
    const response = await fetch(url, {
      method: "POST",
      body: data,
    });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.text();
  };

  // Function to handle third-party API calls
  const handleThirdPartyAPICalls = async (user) => {
    const token = await user.getIdToken();

    const socialLoginJWTURL = process.env.REACT_APP_SOCIAL_LOGIN_JWT_URL;
    const data = new FormData();
    data.append("fbtoken", token);
    data.append("currentUser", JSON.stringify(user));

    try {
      const socialLoginResponse = await postFormData(socialLoginJWTURL, data);
      console.log("Social login JWT response:", socialLoginResponse);

      const socialAuthenticateURL =
        process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;
      const authData = new FormData();
      authData.append("fbtoken", token);

      const authResponseData = await postFormData(
        socialAuthenticateURL,
        authData
      );
      console.log("Social authentication response:", authResponseData);

      // Store response in localStorage
      localStorage.setItem("authResponseData", authResponseData);
      const authResponseJson = JSON.parse(authResponseData);
      const kbeanToken = authResponseJson.token;

      const customToken = await fetchCustomToken(user.uid);

      // Add the TV app session token here
      if (isConnectRoute) {
        console.log("It's a TV sign-in");

        try {
          const expirationTime = Timestamp.fromDate(
            new Date(Date.now() + 1 * 60 * 1000)
          ); // 1 minute from now

          await setDoc(doc(db, "sessions", pairingCode), {
            userId: user.uid,
            email: user.email,
            token: customToken,
            kbeanToken: kbeanToken,
            authenticated: true,
            expiration: expirationTime,
          });

          console.log("Session document created successfully.");
        } catch (error) {
          console.error("Failed to create session document:", error);
        }
      }
    } catch (apiError) {
      console.error(
        "Error authenticating. Please try again. ",
        apiError.message
      );
      setError("Failed to authenticate. Please try again.");
    }
  };

  // Function to handle post-authentication logic
  const handlePostAuth = async (user) => {
    await handleThirdPartyAPICalls(user);
    setAuthenticatedUser(user); // Set authenticated user for profile check
  };

  // Function to handle email/password sign-in
  const handleEmailPasswordSignIn = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      setUser(user);
      await handlePostAuth(user); // Call the post-authentication function
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code); // Convert the error code
      setError(friendlyMessage); // Set the friendly error message
      console.error("Error signing in with email and password:", error.message);
      setLoading(false);
    }
  };

  // Function to handle Google sign-in
  const handleGoogleSignIn = async () => {
    const googleProvider = new GoogleAuthProvider();
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      setUser(user);
      await handlePostAuth(user); // Call the post-authentication function
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code); // Convert the error code
      setError(friendlyMessage); // Set the friendly error message
      console.error("Error signing in with Google:", error.message);
      setLoading(false);
    }
  };

  // Function to handle Apple sign-in
  const handleAppleSignIn = async () => {
    const appleProvider = new OAuthProvider("apple.com");
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;
      setUser(user);
      await handlePostAuth(user); // Call the post-authentication function
    } catch (error) {
      const friendlyMessage = getFriendlyErrorMessage(error.code); // Convert the error code
      setError(friendlyMessage); // Set the friendly error message
      console.error("Error signing in with Apple:", error.message);
      setLoading(false);
    }
  };

  return (
    <>
      {/* Full-screen overlay with circular loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing in...
          </Typography>
        </Box>
      </Backdrop>

      <AuthComponent
        theme={theme}
        pair={isConnectRoute}
        pairingCode={pairingCode} // Pass pairingCode to AuthComponent
        handleEmailPasswordSignIn={handleEmailPasswordSignIn}
        handleGoogleSignIn={handleGoogleSignIn}
        handleAppleSignIn={handleAppleSignIn}
        onCodeComplete={handleCodeComplete} // Pass onCodeComplete to AuthComponent
        loading={loading}
        error={error}
      />
    </>
  );
};

export default SignInPage;
